import {
  MARKETING_CONSENT,
  MARKETING_CONSENT_EXPIRY_IN_MILLISECONDS,
  MARKETING_CONSENT_POPUP_DELAY,
} from "@/constants/general";
import {
  CheckboxFieldConfig,
  TextFieldConfig,
  FieldType,
} from "@/models/formField";
import {
  deleteCookieItem,
  getCookieItem,
  setCookieItem,
} from "@/core/utils/utils.helper";
import { STORAGE_MARKETING_CONSENT } from "@/constants/storage";
import {
  ConditionallyDisplayMarketingModalArgs,
  MarketingConsentStorage,
} from "@/models/marketingConsent";
import { routes } from "@/constants/routes";
import {
  getMarketingConsent,
  getUserSpecificConsent,
} from "@/services/consent.service";

// Function to generate form configuration based on consent items
export const consentCheckboxConfigFormatter = (
  data: any,
  parentClass: string,
): CheckboxFieldConfig => {
  return {
    type: FieldType.CHECKBOX,
    name: data?.consentId, // Unique identifier for the consent item
    label: "",
    defaultValue: [],
    options: [
      {
        label: data?.description, // Description as the label for the checkbox
        value: data?.consentId, // Consent ID as the value of the checkbox
      },
    ],
    className: parentClass, // CSS class for styling
    // Validators are added only for mandatory consent
    validation: {
      validate: (value: any[]) =>
        (value && value.length > 0) ||
        MARKETING_CONSENT.CHECKBOX_MANDATORY_MESSAGE,
    },
  };
};

export const buildEmailidConfig = (
  styles: CSSModuleClasses,
): TextFieldConfig => {
  return {
    type: FieldType.TEXT,
    name: "marketing-consent-textbox", // Unique identifier for the consent item
    label: "",
    defaultValue: "",
    placeholder: MARKETING_CONSENT.EMAIL_PLACEHOLDER,
    validation: {
      required: MARKETING_CONSENT.EMAIL_MANDATORY_MESSAGE,
      pattern: {
        value:
          /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
        message: MARKETING_CONSENT.EMAIL_MANDATORY_MESSAGE,
      },
    },
    className: styles.email_consent, // CSS class for styling
  };
};

// Show marketing consent modal only if consent is not given
export const conditionallyDisplayMarketingModal = ({
  isConsentGiven,
  timeoutId,
  isLoggedIn,
  displayMarketingConsentModal,
  checkboxConfig,
}: ConditionallyDisplayMarketingModalArgs) => {
  if (!isConsentGiven) {
    // Clear previous timeouts
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // For logged in users - show modal immediately
    if (isLoggedIn) {
      displayMarketingConsentModal(checkboxConfig);
    } else {
      //  For logged out users - show modal after a delay
      timeoutId.current = setTimeout(() => {
        displayMarketingConsentModal(checkboxConfig);
      }, MARKETING_CONSENT_POPUP_DELAY);
    }
  }
};

/**
 * To set marketing consent cookie according to the logged in state
 * @param isLoggedIn Logged in state
 * @param consent If consented or not (accepted or closed modal)
 * @param expiry Expiry in milliseconds
 */
export const setMarketingConsentCookie = (
  isLoggedIn: boolean,
  consent: boolean,
  expiry: number,
) => {
  const cookieValue: MarketingConsentStorage = `${isLoggedIn ? "loggedIn" : "anonymous"}_${consent ? "consented" : "dismissed"}`;
  setCookieItem(STORAGE_MARKETING_CONSENT, cookieValue, expiry);
};

/** Get marketing consent info after parsing the cookie */
export const getMarketingConsentInfoFromCookie = () => {
  const cookieValue = getCookieItem(
    STORAGE_MARKETING_CONSENT,
  ) as MarketingConsentStorage | null;

  if (!cookieValue) {
    return null;
  }

  const [isLoggedIn, consent] = cookieValue.split("_");
  return {
    isLoggedInUser: isLoggedIn === "loggedIn",
    consent: consent === "consented",
  };
};

/** Delete marketing consent cookie */
export const deleteMarketingConsentCookie = () => {
  deleteCookieItem(STORAGE_MARKETING_CONSENT);
};

//To set marketing consent cookie on sign out with expiry
export const setMarketingConsentCookieSignout = () => {
  // Set marketing consent cookie with expiry time
  setMarketingConsentCookie(
    false,
    false,
    MARKETING_CONSENT_EXPIRY_IN_MILLISECONDS.SIGNOUT_COOKIE,
  );
};
